import { useUser } from "@/features/user/hooks/use-user";
import { KYCStatusType } from "@/features/kyc/types";
import { useKYC } from "@/features/kyc/hooks/use-kyc";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";

export const useKYCContext = () => {
  const { useUserProfile } = useUser();
  const { companyDetailQuery, companyDetailQueryAdmin } = useCompany();
  const { leUuid } = useCompanyContext();
  const {
    data: user,
    refetch: refetchUserProfile,
    isPending: isPendingUserProfile
  } = useUserProfile();
  const { data: companyUserDetails } = companyDetailQuery(leUuid);
  const { data: companyDetailsAdmin } = companyDetailQueryAdmin(leUuid ?? null);
  const companyDetails = companyUserDetails ?? companyDetailsAdmin;
  const { KYCStateQuery } = useKYC();
  const {
    data: kycSessionStatus,
    refetch: refetchKycSessionStatus,
    isPending: isPendingKycSessionStatus
  } = KYCStateQuery(user?.naturalPerson.UUID ?? null);

  const refetchKYCContext = async () =>
    await Promise.all([refetchUserProfile(), refetchKycSessionStatus()]);
  const kYCStatus =
    leUuid !== null
      ? companyDetails?.olkyPassLevel?.olkypass_status
      : user?.olkyPassLevel.olkypass_status;
  const kYCStatusAudit =
    leUuid !== null
      ? companyDetails?.olkyPassLevel?.olkypass_status_under_audit
      : user?.olkyPassLevel?.olkypass_status_under_audit;
  let status: KYCStatusType | null = null;
  let nextStatus: KYCStatusType | null = null;
  if (kYCStatus)
    status =
      KYCStatusType[
        kYCStatus.english_name?.toLocaleUpperCase().replace(" ", "_") as keyof typeof KYCStatusType
      ];

  if (kYCStatusAudit)
    nextStatus =
      KYCStatusType[
        kYCStatusAudit.english_name
          ?.toLocaleUpperCase()
          .replace(" ", "_") as keyof typeof KYCStatusType
      ];

  let auditPending = false;
  if (
    nextStatus &&
    status !== nextStatus &&
    (kYCStatus?.level as number) < (kYCStatusAudit?.level as number)
  )
    auditPending = true;

  if (nextStatus && (kYCStatus?.level as number) >= (kYCStatusAudit?.level as number)) {
    auditPending = false;
  }
  const isPendingQueries = isPendingUserProfile || isPendingKycSessionStatus;
  return {
    kycSessionStatus,
    status: isPendingQueries ? null : status,
    refetchKYCContext,
    nextStatus: isPendingQueries ? null : nextStatus,
    auditPending,
    kycLevel: kYCStatus?.level,
    kycAuditLevel: kYCStatusAudit?.level
  };
};
